import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSubscription } from "@/hooks/useSubscription";
import { CheckCircle, Loader2 } from "lucide-react";

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkSubscription, currentPlan, isLoading } = useSubscription();

  const sessionId = searchParams.get("session_id");

  // Refresh subscription status after successful payment
  useEffect(() => {
    if (sessionId) {
      // Give Stripe a moment to process, then refresh
      const timer = setTimeout(() => {
        checkSubscription();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionId, checkSubscription]);

  const planName = currentPlan === "pro" 
    ? t("pricingPage.plans.pro.name") 
    : currentPlan === "basic" 
      ? t("pricingPage.plans.basic.name")
      : t("pricingPage.plans.free.name");

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full text-center">
        <CardHeader className="pb-4">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            {isLoading ? (
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            ) : (
              <CheckCircle className="w-8 h-8 text-primary" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {t("subscription.success.title")}
          </CardTitle>
          <CardDescription className="text-lg">
            {t("subscription.success.welcome", { plan: planName })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {t("subscription.success.description")}
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate("/dashboard")} size="lg">
              {t("subscription.success.goDashboard")}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/settings/subscription")}
            >
              {t("subscription.success.viewSubscription")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
